/* UI-DESIGN: LB-84 keeps the character passport, briefing and round notes together on the lightbox.
   Preview without a backend: /passport?preview=sample */
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link, useSearchParams } from "react-router-dom";
import clsx from "clsx";
import cn from "classnames";
import type { RootState } from "../../store/store";
import type { Player } from "../../types/game";
import PlayerForm from "./PlayerForm";
import PeopleMap from "./PeopleMap";
import NoteField from "./NoteField";
import { useDossierNotes } from "./useDossierNotes";
import type { DossierNotes } from "./useDossierNotes";
import { caseArtFor } from "../../components/lightbox/caseArt";
import LightboxSurface from "../../components/lightbox/LightboxSurface";
import GlassPanel from "../../components/lightbox/GlassPanel";
import LightboxButton from "../../components/lightbox/LightboxButton";
import { initialTypeface } from "../../components/lightbox/typefaces";
import { useGetPlayerQuery } from "../../reducers/apiSlice";
import { SAMPLE_PLAYER } from "./samplePlayer";
import styles from "./Passport.module.scss";

/* UI-DESIGN: 2026-10-25 (Taco picked B3: "easy to nav and get information while on
   their phone") three short tabs so each section fits about one phone screen:
   who you are, the round updates, and the people map. Passport trivia (birth
   date, number) drops to one line of fine print. */
const TABS = [
  { key: "you", label: "You" },
  { key: "tonight", label: "Tonight" },
  { key: "people", label: "People" },
] as const;

type TabKey = (typeof TABS)[number]["key"];

const YouTab = ({ player }: { player: Player }) => {
  const facts = [
    player.role.nationality,
    player.role.date_of_birth && `Born ${player.role.date_of_birth}`,
    player.role.passport_number && `Passport ${player.role.passport_number}`,
  ].filter(Boolean);
  return (
    <>
      <h2 className={styles.sectionTitle}>Who you are</h2>
      <p className={styles.dossierText}>
        {player.role.character_info?.trim() || player.role.description?.trim() ||
          "Your character details will appear here once roles have been assigned."}
      </p>
      {facts.length > 0 && <p className={styles.fine}>{facts.join(". ")}.</p>}
      {player.role.character_sheet_url ? (
        <a
          className={styles.attachmentLink}
          href={player.role.character_sheet_url}
          target="_blank"
          rel="noopener noreferrer"
        >
          Full character sheet
        </a>
      ) : null}
    </>
  );
};

const TonightTab = ({ player, notes }: { player: Player; notes: DossierNotes }) => {
  const updates = player.round_updates ?? [];
  return (
    <>
      <h2 className={styles.sectionTitle}>Tonight</h2>
      {updates.length === 0 ? (
        <p className={styles.logEmpty}>Nothing yet. Updates land here each round.</p>
      ) : (
        /* UI-DESIGN: newest round first, the one you need right now */
        <ol className={styles.logList} reversed>
          {updates.map((entry, i) => ({ entry, round: i + 1 })).reverse().map(({ entry, round }, i) => (
            <li key={round} className={clsx(styles.logEntry, i === 0 && styles.logEntryNew)}>
              <span className={styles.logRound}>Round {round}</span>
              <p className={styles.logEntryBody}>{entry}</p>
            </li>
          ))}
        </ol>
      )}
      <NoteField label="My notes for tonight" value={notes.notes.tonight} onChange={notes.setTonight} notes={notes} />
    </>
  );
};

const PeopleTab = ({ player, notes }: { player: Player; notes: DossierNotes }) => {
  const relationships = player.role.relationships ?? [];
  return (
    <>
      <h2 className={styles.sectionTitle}>People you know</h2>
      {relationships.length === 0
        ? <p className={styles.logEmpty}>No one on file yet.</p>
        : <PeopleMap relationships={relationships} notes={notes} />}
    </>
  );
};

/* UI-DESIGN: keep draft state while changing tabs, but remount for each game/player. */
const DossierContent = ({ player, gameId, activeTab }: { player: Player; gameId: string | null; activeTab: TabKey }) => {
  const notes = useDossierNotes(gameId, player.objectId);
  return activeTab === "you" ? <YouTab player={player} />
    : activeTab === "tonight" ? <TonightTab player={player} notes={notes} />
    : <PeopleTab player={player} notes={notes} />;
};

const Tabs = ({
  activeTab,
  setActiveTab,
  unread,
}: {
  activeTab: TabKey;
  setActiveTab: (key: TabKey) => void;
  unread: boolean;
}) => (
  /* UI-DESIGN: B3 paper index dividers standing up behind the glass; the open one rises */
  <div className={styles.tabs} role="tablist" aria-label="Dossier sections">
    {TABS.map((tab, i) => (
      <button
        key={tab.key}
        type="button"
        role="tab"
        id={`passport-tab-${tab.key}`}
        aria-controls={`passport-panel-${tab.key}`}
        tabIndex={activeTab === tab.key ? 0 : -1}
        onKeyDown={(event) => {
          const next = event.key === "ArrowRight" ? (i + 1) % TABS.length
            : event.key === "ArrowLeft" ? (i + TABS.length - 1) % TABS.length
            : event.key === "Home" ? 0 : event.key === "End" ? TABS.length - 1 : null;
          if (next === null) return;
          event.preventDefault();
          setActiveTab(TABS[next].key);
          document.getElementById(`passport-tab-${TABS[next].key}`)?.focus();
        }}
        aria-selected={activeTab === tab.key}
        onClick={() => setActiveTab(tab.key)}
        className={cn(styles.tabsButton, {
          [styles.tabsButtonActive]: activeTab === tab.key,
        })}
      >
        {tab.label}
        {tab.key === "tonight" && unread && <span className={styles.unread}><span className={styles.srOnly}>, new update</span></span>}
      </button>
    ))}
  </div>
);

/* UI-DESIGN: the red dot on Tonight clears once you've opened it; remembered
   per character in this browser only, so a blocked storage just shows the dot */
const seenKey = (id: string) => `clue-dossier-seen-${id}`;
const readSeen = (id: string) => { try { return Number(localStorage.getItem(seenKey(id)) ?? 0); } catch { return 0; } };
const writeSeen = (id: string, n: number) => { try { localStorage.setItem(seenKey(id), String(n)); } catch { /* storage blocked */ } };

const initialsOf = (name: string) => name.split(/\s+/).filter((w) => /^[A-Z]/.test(w) && !/\.$/.test(w)).map((w) => w[0]).join("").slice(0, 2) || name.slice(0, 1);

const PassportPage = () => {
  const reduxPlayer = useSelector((state: RootState) => state.game.player);
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabKey>("you");
  const game = useSelector((state: RootState) => state.game.game);

  /* UI-DESIGN: hit /passport?preview=sample to design-review without backend */
  const isPreview = searchParams.get("preview") === "sample";
  const accessCode = searchParams.get("access_code");
  const gameId = searchParams.get("gameId");
  const [isVisible, setIsVisible] = useState(() => document.visibilityState === "visible");
  const canRefresh = !!accessCode && !isPreview;
  /* UI-DESIGN: resolve the URL even with another player cached; never show the wrong dossier. */
  const { currentData, isFetching, isError, refetch } = useGetPlayerQuery(accessCode ?? "", {
    skip: !canRefresh,
    pollingInterval: isVisible ? 10_000 : 0,
    refetchOnMountOrArgChange: true,
  });

  /* UI-DESIGN: returning players catch up without losing their open tab; hidden dossiers stop polling.
     Page listeners work without changing the shared store's listener setup. */
  useEffect(() => {
    const refresh = () => {
      if (canRefresh && document.visibilityState === "visible" && !isFetching) void refetch();
    };
    const onVisibilityChange = () => {
      setIsVisible(document.visibilityState === "visible");
      refresh();
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", refresh);
    window.addEventListener("online", refresh);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", refresh);
      window.removeEventListener("online", refresh);
    };
  }, [canRefresh, isFetching, refetch]);
  const player: Player | null = isPreview ? SAMPLE_PLAYER : accessCode
    ? (currentData?.player ? { ...currentData.player, round_updates: currentData.round_updates ?? [] } : null)
    : reduxPlayer;

  const roleId = player?.role?.objectId ?? "";
  const updateCount = player?.round_updates?.length ?? 0;
  const [seen, setSeen] = useState(0);
  useEffect(() => { if (roleId) setSeen(readSeen(roleId)); }, [roleId]);
  useEffect(() => {
    if (activeTab === "tonight" && roleId && updateCount > seen) { writeSeen(roleId, updateCount); setSeen(updateCount); }
  }, [activeTab, roleId, updateCount, seen]);
  const photo = caseArtFor(game?.mystery?.name);

  return (
    <LightboxSurface mode="static" typeface={initialTypeface()} onUnavailable={() => undefined}>
    <main className={styles.page}>
      <div className={styles.inner}>
        {gameId && <Link className={styles.returnLink} to={`/game/${encodeURIComponent(gameId)}`}>Back to game <span aria-hidden>→</span></Link>}
        {/* UI-DESIGN: background refresh never replaces a readable dossier with a loading screen. */}
        {canRefresh && isError && currentData?.player && (
          <GlassPanel className={styles.refreshNotice}>
            <p role="status">Your passport couldn’t be refreshed. Showing the last available details.</p>
            <LightboxButton tone="secondary" isDisabled={isFetching} onPress={() => { void refetch(); }}>
              {isFetching ? "Refreshing…" : "Try again"}
            </LightboxButton>
          </GlassPanel>
        )}
        {canRefresh && isFetching && !currentData?.player ? (
          <GlassPanel className={styles.entryForm}><p role="status">Opening your passport…</p></GlassPanel>
        ) : !player ? <PlayerForm invalidLink={isError} /> : !player.role ? (
          <GlassPanel className={styles.entryForm}>
            <h1 className={styles.headerTitle}>Your character is on its way</h1>
            <p className={styles.headerSubtitle}>Your host hasn’t assigned your role yet. Come back after roles are sent.</p>
          </GlassPanel>
        ) : <>

        {/* UI-DESIGN: 2026-10-25 B3 header: your photo as a small print beside the name */}
        <header className={clsx(styles.header, "ui-reveal")}>
          <div className={styles.photo} aria-hidden>
            {photo ? <img src={photo} alt="" /> : <span>{initialsOf(player.role.name)}</span>}
          </div>
          <div className={styles.headerText}>
            <h1 className={styles.headerTitle}>{player.role.name}</h1>
            {player.role.description?.trim() && <p className={styles.headerSubtitle}>{player.role.description.trim().split(/(?<=\.)\s/)[0]}</p>}
          </div>
        </header>

        <div className={clsx(styles.stack, "ui-reveal", "ui-reveal-1")}>
          <Tabs activeTab={activeTab} setActiveTab={setActiveTab} unread={activeTab !== "tonight" && updateCount > seen} />
          <GlassPanel
            as="section"
            role="tabpanel"
            id={`passport-panel-${activeTab}`}
            aria-labelledby={`passport-tab-${activeTab}`}
            tabIndex={0}
            className={styles.content}
          >
            <DossierContent key={JSON.stringify([isPreview ? "preview" : gameId, player.objectId])}
              player={player} gameId={isPreview ? "preview" : gameId} activeTab={activeTab} />
          </GlassPanel>
        </div>
        </>}
      </div>
    </main>
    </LightboxSurface>
  );
};

export default PassportPage;
